/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { riskResultsMarkFalsePositive } from "../funcs/riskResultsMarkFalsePositive.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  MarkRiskResultsFalsePositiveRequest,
  MarkRiskResultsFalsePositiveSecurity,
} from "../models/operations/markriskresultsfalsepositive.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RiskMarkResultsFalsePositiveMutationVariables = {
  request: MarkRiskResultsFalsePositiveRequest;
  security?: MarkRiskResultsFalsePositiveSecurity | undefined;
  options?: RequestOptions;
};

export type RiskMarkResultsFalsePositiveMutationData = void;

export type RiskMarkResultsFalsePositiveMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * markRiskResultsFalsePositive risk
 *
 * @remarks
 * Mark one or more risk results as manually-reviewed false positives. Distinct from exclusions: this suppresses the specific results picked, not future findings matching a rule.
 */
export function useRiskMarkResultsFalsePositiveMutation(
  options?: MutationHookOptions<
    RiskMarkResultsFalsePositiveMutationData,
    RiskMarkResultsFalsePositiveMutationError,
    RiskMarkResultsFalsePositiveMutationVariables
  >,
): UseMutationResult<
  RiskMarkResultsFalsePositiveMutationData,
  RiskMarkResultsFalsePositiveMutationError,
  RiskMarkResultsFalsePositiveMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRiskMarkResultsFalsePositiveMutation(client, options),
    ...options,
  });
}

export function mutationKeyRiskMarkResultsFalsePositive(): MutationKey {
  return ["@gram/client", "results", "markFalsePositive"];
}

export function buildRiskMarkResultsFalsePositiveMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RiskMarkResultsFalsePositiveMutationVariables,
  ) => Promise<RiskMarkResultsFalsePositiveMutationData>;
} {
  return {
    mutationKey: mutationKeyRiskMarkResultsFalsePositive(),
    mutationFn: function riskMarkResultsFalsePositiveMutationFn({
      request,
      security,
      options,
    }): Promise<RiskMarkResultsFalsePositiveMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(riskResultsMarkFalsePositive(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
